export const useAuthStore = defineStore('auth', () => {
  const userStore = useUserStore();
  const { user } = storeToRefs(userStore);

  const isAuthenticated = computed(() => user.value !== null);

  const { $sellebrate } = useNuxtApp();
  const { add: addSnackbar } = useSnackbar();

  const loggingIn = ref(false);
  const login = async (email: string, password: string) => {
    loggingIn.value = true;

    try {
      await $sellebrate('/users/login', {
        method: 'post',
        onResponseError: undefined,
        body: new URLSearchParams({ password, username: email }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      try {
        await userStore.fetchMe();
        // After login, route based on presence of companyId on the user
        if (!user.value?.companyId) {
          await navigateTo('/wizard');
        }
        else {
          await navigateTo({ name: 'index' });
        }
      }
      catch (err: any) {
        const detail: any = err?.data?.detail;
        const code: string | undefined = typeof detail === 'object' ? detail?.code : undefined;
        if (err?.status === 403 && code === 'EMAIL_NOT_VERIFIED') {
          addSnackbar({
            autoClose: true,
            variant: 'info',
            title: 'Verify your email',
            description: 'We sent you a verification link. You can resend it below.',
          });
          await navigateTo({ path: '/verify', query: { email } });
          return;
        }
        if (err?.status === 403 && code === 'ACCOUNT_INACTIVE') {
          addSnackbar({
            autoClose: true,
            variant: 'error',
            title: 'This account has been disabled. Please contact support.',
          });
          await navigateTo({ name: 'login' });
          return;
        }
        throw err;
      }
    }
    catch (err: any) {
      // Handle credential errors gracefully so the global error handler doesn't show raw FetchError
      const detailRaw: any = err?.data?.detail || err?.data?.message;
      const code: string | undefined = typeof detailRaw === 'object' ? detailRaw?.code : undefined;
      const detail: string | undefined = typeof detailRaw === 'object' ? detailRaw?.message : detailRaw;

      if (err?.status === 403 && code === 'EMAIL_NOT_VERIFIED') {
        addSnackbar({
          autoClose: true,
          variant: 'info',
          title: 'Verify your email',
          description: 'We sent you a verification link. You can resend it below.',
        });
        await navigateTo({ path: '/verify', query: { email } });
        return;
      }

      if (err?.status === 403 && code === 'ACCOUNT_INACTIVE') {
        addSnackbar({
          autoClose: false,
          variant: 'error',
          title: 'This account has been disabled. Please contact support.',
        });
        return;
      }
      if (err?.status === 401) {
        addSnackbar({
          autoClose: true,
          variant: 'error',
          title: 'Invalid email or password',
          description: 'Double-check your credentials and try again.',
        });
        return;
      }

      // Generic fallback for other unexpected login failures
      addSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Login failed',
        description: detail || 'Please try again.',
      });
      return;
    }
    finally {
      loggingIn.value = false;
    }
  };

  const logout = async () => {
    await $sellebrate('/users/logout', { ignoreResponseError: true });
    user.value = null;
    await navigateTo({ name: 'login' });
  };

  const refresh = async () => {
    await $sellebrate('/users/refresh', {
      retry: false,
      onResponseError: async () => await logout(),
    });
  };

  return { user, login, logout, refresh, loggingIn, isAuthenticated };
});
